import { userService } from "@/app/api/user";
import {
  AssessmentQuestionFields,
  type RelatedRoleDetail,
  type ReviewPageBasicQuestion,
  type GeneratableFields,
  type GenerateCompetencyFieldBody,
  type GenerateResponse,
  GenerationStatus,
  type PublishedScreenerBuilderUpdateBody,
  type ReviewPageDraftQuestion,
  type SaveCompetencyPayload,
  type SaveCompetencyResponse,
  type ScreenerBuilderDataMap,
  type ScreenerBuilderPublishResponse,
  type ScreenerBuilderResponse,
  type ScreenerBuilderReviewPageResponse,
  ScreenerBuilderStep,
  type SlimScreenerBuilderDetails,
  type WithBaseGenerationRequirements,
} from "@/types/screener-builder";
import { requestWithRetry, type RetryCallback } from "@/utils/api";
import { defaultShouldRetry } from "@/utils/react-query";
import {
  validateGeneratedContent,
  prepareGenerateRetryContext,
} from "@/utils/screener-builder";

export const getScreenerBuilderDraft = async (
  screenerId = "",
): Promise<ScreenerBuilderResponse> => {
  try {
    const response =
      await userService.axiosWithUserAuth.get<ScreenerBuilderResponse>(
        `/screener-builder/${screenerId}`,
      );
    return response.data;
  } catch (error) {
    console.error("Error fetching screener builder", error);
    throw error;
  }
};

export const createScreenerBuilder = async (
  body: ScreenerBuilderDataMap[ScreenerBuilderStep.Position],
) => {
  try {
    const response = await userService.axiosWithUserAuth.post<{
      screenerId: string;
      currentPage: ScreenerBuilderStep;
      generationStatus: GenerationStatus;
      published: boolean;
    }>(`/screener-builder`, body);

    return response.data;
  } catch (error) {
    console.error("Error creating screener builder", error);
    throw error;
  }
};

export const updateScreenerBuilderStep = async <T extends ScreenerBuilderStep>(
  screenerId: string,
  step: T,
  body: ScreenerBuilderDataMap[T],
): Promise<
  T extends ScreenerBuilderStep.Review
    ? ScreenerBuilderReviewPageResponse
    : ScreenerBuilderResponse
> => {
  try {
    const response = await userService.axiosWithUserAuth.put<
      T extends ScreenerBuilderStep.Review
        ? ScreenerBuilderReviewPageResponse
        : ScreenerBuilderResponse
    >(`/screener-builder/${screenerId}/${step}`, body);

    return response.data;
  } catch (error) {
    console.error(`Error updating screener builder ${step}`, error);
    throw error;
  }
};

export const getSlimScreenerBuilderDetails = async (
  screenerId: string,
): Promise<SlimScreenerBuilderDetails> => {
  try {
    const response =
      await userService.axiosWithUserAuth.get<SlimScreenerBuilderDetails>(
        `/screener/slim/${screenerId}`,
      );

    return response.data;
  } catch (error) {
    console.error("Error fetching slim screener details", error);
    throw error;
  }
};

export const getPreview = async (screenerId: string): Promise<string> => {
  try {
    const response = await userService.axiosWithUserAuth.get<{
      screenerUrl: string;
    }>(`/screener/preview/${screenerId}`, {
      timeout: 90000, // 90 seconds
    });
    return response.data.screenerUrl;
  } catch (error) {
    console.error("Error fetching screener builder preview link", error);
    throw error;
  }
};

export const publishScreener = async (screenerId: string) => {
  try {
    const response =
      await userService.axiosWithUserAuth.get<ScreenerBuilderPublishResponse>(
        `/screener/publish/${screenerId}`,
        {
          timeout: 90000, // 90 seconds
        },
      );
    return response.data;
  } catch (error) {
    console.error("Error publishing screener", error);
    throw error;
  }
};

export const copyScreener = async (screenerId: string) => {
  try {
    // The full response here is a screener in a format we don't use on the
    // front end. Because the redirect after copy has to fetch builder info
    // regardless we don't try to force this data into a format the FE can work
    // with, just grab the ID off the copied screener and pass it along
    const response = await userService.axiosWithUserAuth.get<{ id: string }>(
      `/screener/copy/${screenerId}`,
    );
    return response.data.id;
  } catch (error) {
    console.error("Error copying screener", error);
    throw error;
  }
};

export const archiveScreener = async (screenerId: string) => {
  try {
    const response = await userService.axiosWithUserAuth.delete<string>(
      `/screener/${screenerId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error archiving screener", error);
    throw error;
  }
};

export const updatePublishedScreener = async (
  screenerId: string,
  body: PublishedScreenerBuilderUpdateBody,
) => {
  try {
    const response =
      await userService.axiosWithUserAuth.put<ScreenerBuilderResponse>(
        `/screener/${screenerId}`,
        body,
      );
    return response.data;
  } catch (error) {
    console.error("Error updating published screener", error);
    throw error;
  }
};

const generateCompetencyField = async <T extends GeneratableFields>(
  field: T,
  generateInput: GenerateCompetencyFieldBody<T>,
): Promise<GenerateResponse<T>> => {
  try {
    const response = await userService.axiosWithUserAuth.post<
      GenerateResponse<T>
    >("/screener-builder/self-serve/generate", generateInput);

    if (field === AssessmentQuestionFields.Question) {
      return {
        content: (
          response.data as GenerateResponse<AssessmentQuestionFields.Question>
        ).content.map((question) => ({ ...question, is_selected: false })),
      } as GenerateResponse<T>;
    }

    return response.data;
  } catch (error) {
    console.error("Error generating competency field", error);
    throw error;
  }
};

export const generateCompetencyFieldWithRetry = async <
  T extends GeneratableFields,
>(
  field: T,
  originalInput: GenerateCompetencyFieldBody<T>,
) => {
  const VALIDATION_ERROR_CAUSE = Symbol("Validation Error");
  let currentInput = originalInput;

  const requestFn = async () => {
    const response = await generateCompetencyField(field, currentInput);

    const { isValid, errors } = validateGeneratedContent(
      field,
      response.content,
    );

    if (isValid) {
      return response;
    }

    currentInput = {
      ...currentInput,
      priorOutput: prepareGenerateRetryContext(field, response),
      errors,
    };

    throw new Error("LLM output failed programmatic validation", {
      cause: VALIDATION_ERROR_CAUSE,
    });
  };

  const shouldRetryCallback: RetryCallback = (
    error,
    failureCount,
    lastDelay,
  ) => {
    if (failureCount >= 3) {
      return [false, 0];
    }

    const shouldRetry =
      error.cause === VALIDATION_ERROR_CAUSE ||
      defaultShouldRetry(failureCount, error);

    return [shouldRetry, lastDelay * 2];
  };

  return requestWithRetry(requestFn, shouldRetryCallback);
};

interface ValidateStepOneInput {
  competencyDefinition: string;
  competencyName: string;
  question: string;
  step: 1;
}

interface ValidateStepTwoInput extends Omit<ValidateStepOneInput, "step"> {
  highScoringRubric: string;
  lowScoringRubric: string;
  mediumScoringRubric: string;
  step: 2;
}

export const validateCompetencyStep = async (
  stepData: WithBaseGenerationRequirements<
    ValidateStepOneInput | ValidateStepTwoInput
  >,
) =>
  requestWithRetry(async () => {
    const response = await userService.axiosWithUserAuth.post<{
      validated: boolean;
      warning: string[];
    }>("/screener-builder/self-serve/validate", stepData);

    return response.data;
  });

export const saveCompetency = async (
  payload: SaveCompetencyPayload,
): Promise<SaveCompetencyResponse> =>
  requestWithRetry(async () => {
    const response =
      await userService.axiosWithUserAuth.post<SaveCompetencyResponse>(
        "/screener-builder/self-serve/assessment-question",
        payload,
      );

    return response.data;
  });

export const updateCompetency = async (
  competencyId: string,
  payload: SaveCompetencyPayload,
): Promise<SaveCompetencyResponse> =>
  requestWithRetry(async () => {
    const response =
      await userService.axiosWithUserAuth.put<SaveCompetencyResponse>(
        `/screener-builder/self-serve/assessment-question/${competencyId}`,
        payload,
      );

    return response.data;
  });

export const deleteCompetency = async (competencyId: string) =>
  requestWithRetry(async () => {
    const response = await userService.axiosWithUserAuth.delete(
      `/screener-builder/self-serve/assessment-question/${competencyId}`,
    );

    return response.data;
  });

export const saveBasicQuestion = (
  payload: ReviewPageDraftQuestion,
  screenerId: string,
) =>
  requestWithRetry(async () => {
    const response =
      await userService.axiosWithUserAuth.post<ReviewPageBasicQuestion>(
        `/screener-builder/self-serve/basic-question`,
        { ...payload, screenerId },
      );

    return response.data;
  });

export const updateBasicQuestion = (
  payload: ReviewPageBasicQuestion,
  screenerId: string,
) =>
  requestWithRetry(async () => {
    const response =
      await userService.axiosWithUserAuth.put<ReviewPageBasicQuestion>(
        `/screener-builder/self-serve/basic-question/${payload.id}`,
        { ...payload, screenerId },
      );

    return response.data;
  });

export const deleteBasicQuestion = (questionId: string) =>
  requestWithRetry(async () => {
    const response = await userService.axiosWithUserAuth.delete(
      `/screener-builder/self-serve/basic-question/${questionId}`,
    );

    return response.data;
  });

export const getReviewPageData = async (
  screenerId: string,
): Promise<ScreenerBuilderReviewPageResponse> => {
  try {
    const response =
      await userService.axiosWithUserAuth.get<ScreenerBuilderReviewPageResponse>(
        `/screener-builder/${screenerId}/review-page`,
      );
    return response.data;
  } catch (error) {
    console.error("Error fetching review page data", error);
    throw error;
  }
};

export const discardChanges = async (screenerId: string) => {
  try {
    const response = await userService.axiosWithUserAuth.delete(
      `/screener/${screenerId}/draft`,
    );

    return response.data;
  } catch (error) {
    console.error("Error discarding changes", error);
    throw error;
  }
};

export const searchRelatedScreeners = async (
  query: string,
  excludeScreenerId?: string,
): Promise<RelatedRoleDetail[]> => {
  const params = new URLSearchParams({ query });
  if (excludeScreenerId) {
    params.set("excludeScreenerId", excludeScreenerId);
  }
  const response = await userService.axiosWithUserAuth.get<RelatedRoleDetail[]>(
    `/screener/search-related?${params.toString()}`,
  );
  return response.data;
};
